<script setup>
import { computed, onMounted } from 'vue'

const props = defineProps({
  formId:  { type: String, required: true },
  baseUrl: { type: String, default: 'https://www.tfaforms.com' },
  // Iframe title for accessibility (announced to screen readers).
  title:   { type: String, default: 'Form' },
})

const formUrl = computed(() => `${props.baseUrl}/${props.formId}`)

// FormAssembly serves its resize helper once per host; a fixed id keeps SPA route
// changes from injecting duplicates.
const resizeHelperId = 'fa-iframe-resize-helper'

onMounted(() => {
  // Client-only (onMounted never runs during vite-ssg prerender). The helper listens
  // for the embedded form's postMessage and grows the iframe to fit its content.
  if (document.getElementById(resizeHelperId)) return
  const script = document.createElement('script')
  script.id = resizeHelperId
  script.src = `${props.baseUrl}/js/iframe_resize_helper.js`
  script.async = true
  document.body.appendChild(script)
})
</script>

<template>
  <div class="fa-embed">
    <iframe :src="formUrl" :title="title" class="frame"></iframe>
    <noscript>
      <a :href="formUrl" target="_blank" rel="noopener">Complete this form</a>
    </noscript>
  </div>
</template>

<style lang="scss" scoped>
.fa-embed {
  width: 100%;

  .frame {
    width: 100%;
    // The hosted form (raw /{id} URL) does NOT postMessage its height, so the parent
    // resize helper never fires — and cross-origin rules stop us measuring the form
    // ourselves — so there is no way to auto-fit. Instead we size the frame to
    // comfortably contain the form (intro + 4 fields + reCAPTCHA + submit). Scrolling
    // is left enabled, so an expanded state (reCAPTCHA image challenge, validation
    // errors) can still scroll rather than clip. Tune this to the live form height.
    // For true auto-fit, publish the form via FormAssembly's "Embed as iframe" option
    // (it injects the height-poster); the resize helper above then drives the height
    // and overrides this floor automatically.
    min-height: 560px;
    border: 0;
  }
}
</style>
